import React, { useEffect, useRef, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, Search, ChevronDown, ChevronUp, Upload, Trash2, RefreshCw, CheckCircle2, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const PAGE = 30;
const STATUS = {
  ready: { label: "Hazır", cls: "bg-emerald-500/15 text-emerald-300" },
  missing: { label: "Eksik", cls: "bg-amber-500/15 text-amber-300" },
  inaccessible: { label: "Erişilemiyor", cls: "bg-rose-500/15 text-rose-300" },
  unverified: { label: "Doğrulanmadı", cls: "bg-sky-500/15 text-sky-300" },
};

export default function AdminImages() {
  const [tab, setTab] = useState("missing");
  const [exam, setExam] = useState("");
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [data, setData] = useState({ counts: {}, total: 0, items: [] });
  const [limit, setLimit] = useState(PAGE);
  const [openId, setOpenId] = useState(null);

  const load = async () => {
    const { data: d } = await api.get("/admin/images/questions", {
      params: { tab, exam_level: exam || undefined, q: search || undefined, limit },
    });
    setData(d);
  };
  useEffect(() => { load().catch(() => toast.error("Liste yüklenemedi")); /* eslint-disable-next-line */ }, [tab, exam, search, limit]);

  const onUpdated = (summary) => {
    if (summary.status !== tab) {
      toast.success(summary.status === "complete"
        ? `${summary.ref} tamamlandı → "Tamamlandı" listesine taşındı.`
        : `${summary.ref} tekrar "Görseli Eksik" listesine döndü.`);
      setOpenId(null);
    }
    load();
  };

  const c = data.counts || {};
  return (
    <div className="space-y-4 min-w-0 max-w-full" data-testid="admin-images-page">
      <div>
        <h1 className="font-display text-3xl tracking-tight">Görsel Yönetimi</h1>
        <p className="text-sm text-slate-400 mt-1">
          Görsel bloğu olan sorular. Bir soru, tüm görselleri yüklenip depolamadan doğrulandığında tamamlanmış sayılır.
        </p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <Stat label="Görseli eksik soru" value={c.missing_questions} tid="admin-images-count-missing" />
        <Stat label="Tamamlanan soru" value={c.complete_questions} tid="admin-images-count-complete" />
        <Stat label="Eksik görsel" value={c.missing_images} tid="admin-images-count-missing-images" />
      </div>

      <div className="flex flex-wrap items-center gap-2 border-b border-[#262626]">
        {[["missing", "Görseli Eksik"], ["complete", "Tamamlandı"]].map(([k, l]) => (
          <button key={k} onClick={() => { setTab(k); setOpenId(null); setLimit(PAGE); }}
            data-testid={`admin-images-tab-${k}`}
            className={`px-4 py-2 text-sm rounded-t-md transition-colors ${tab === k ? "bg-[#EA580C] text-white" : "text-slate-300 hover:bg-[#171717]"}`}>
            {l}
          </button>
        ))}
        <div className="flex-1" />
        <select value={exam} onChange={(e) => { setExam(e.target.value); setLimit(PAGE); }}
          data-testid="admin-images-exam-filter"
          className="px-3 py-1.5 mb-1 rounded-md bg-[#171717] border border-[#262626] text-sm">
          <option value="">Tüm sınavlar</option>
          <option value="LGS">LGS</option>
          <option value="TYT">TYT</option>
          <option value="AYT">AYT</option>
        </select>
        <div className="relative mb-1 w-full sm:w-auto">
          <Search size={14} className="absolute left-2.5 top-2.5 text-slate-500" />
          <input value={query} onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") { setSearch(query.trim()); setLimit(PAGE); } }}
            onBlur={() => setSearch(query.trim())}
            placeholder="MAT ref, konu veya metin"
            data-testid="admin-images-search"
            className="pl-8 pr-3 py-1.5 rounded-md bg-[#171717] border border-[#262626] text-sm outline-none focus:border-[#EA580C] w-full sm:w-56" />
        </div>
      </div>

      <div className="space-y-2" data-testid="admin-images-list">
        {data.items.map((q) => (
          <QuestionRow key={q.question_id} q={q} open={openId === q.question_id}
            onToggle={() => setOpenId(openId === q.question_id ? null : q.question_id)}
            onUpdated={onUpdated} />
        ))}
        {data.items.length === 0 && (
          <div className="border border-[#262626] rounded-lg p-8 text-center text-slate-500" data-testid="admin-images-empty">
            Bu listede soru yok.
          </div>
        )}
        {data.total > data.items.length && (
          <button onClick={() => setLimit(limit + PAGE)} data-testid="admin-images-load-more"
            className="w-full py-2 rounded-md border border-[#262626] text-sm text-slate-300 hover:border-[#EA580C]">
            Daha fazla göster ({data.items.length}/{data.total})
          </button>
        )}
      </div>
    </div>
  );
}

function Stat({ label, value, tid }) {
  return (
    <div className="border border-[#262626] rounded-lg p-4 bg-[#0F0F0F]">
      <div className="text-xs text-slate-500">{label}</div>
      <div className="font-display text-2xl mt-1" data-testid={tid}>{value ?? "—"}</div>
    </div>
  );
}

function QuestionRow({ q, open, onToggle, onUpdated }) {
  const [busy, setBusy] = useState(false);
  const verify = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/images/questions/${q.question_id}/verify`);
      toast.success("Görseller yeniden doğrulandı.");
      onUpdated(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Doğrulanamadı");
    } finally { setBusy(false); }
  };
  return (
    <div className="border border-[#262626] rounded-lg overflow-hidden" data-testid={`admin-images-row-${q.ref}`}>
      <button onClick={onToggle} data-testid={`admin-images-toggle-${q.ref}`}
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-[#171717]">
        <span className="font-mono text-xs text-[#EA580C] w-20 shrink-0">{q.ref}</span>
        <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#262626] text-slate-300 shrink-0">{q.exam_level || "—"}</span>
        <div className="flex-1 min-w-0">
          <div className="text-xs text-slate-400 truncate">{q.topic_name}</div>
          <div className="text-sm truncate">{q.snippet || "—"}</div>
        </div>
        <span data-testid={`admin-images-progress-${q.ref}`}
          className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full shrink-0 ${q.status === "complete" ? "bg-emerald-500/15 text-emerald-300" : "bg-amber-500/15 text-amber-300"}`}>
          {q.status === "complete" ? <CheckCircle2 size={12} /> : <AlertTriangle size={12} />}
          {q.missing_images}/{q.total_images} eksik
        </span>
        {open ? <ChevronUp size={14} className="text-slate-500" /> : <ChevronDown size={14} className="text-slate-500" />}
      </button>
      {open && (
        <div className="px-4 pb-4 pt-2 border-t border-[#262626] space-y-3" data-testid={`admin-images-detail-${q.ref}`}>
          {q.image_blocks.map((b, n) => (
            <ImageBlockSlot key={b.block_index} q={q} b={b} n={n + 1} onUpdated={onUpdated} />
          ))}
          <button onClick={verify} disabled={busy} data-testid={`admin-images-verify-${q.ref}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-[#262626] text-xs hover:border-[#EA580C] disabled:opacity-50">
            <RefreshCw size={12} /> Depolamadan yeniden doğrula
          </button>
        </div>
      )}
    </div>
  );
}

function ImageBlockSlot({ q, b, n, onUpdated }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const tid = `${q.ref}-${b.block_index}`;
  const st = STATUS[b.status] || STATUS.missing;

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const up = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.put(`/admin/images/questions/${q.question_id}/blocks/${b.block_index}`,
        { path: up.data.path, description: b.fingerprint });
      toast.success(`Görsel ${n} kaydedildi ve doğrulandı.`);
      onUpdated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Görsel kaydedilemedi");
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Görsel ${n} kaldırılsın mı?`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/images/questions/${q.question_id}/blocks/${b.block_index}/remove`,
        { description: b.fingerprint });
      toast.success(`Görsel ${n} kaldırıldı.`);
      onUpdated(data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kaldırılamadı");
    } finally { setBusy(false); }
  };

  return (
    <div className="grid sm:grid-cols-[160px_1fr] gap-3 rounded-md border border-[#262626] bg-[#0F0F0F] p-3"
      data-testid={`admin-images-slot-${tid}`}>
      {b.image_path ? (
        <img alt={`gorsel-${n}`} src={`${BACKEND_URL}/api/uploads/file?path=${encodeURIComponent(b.image_path)}`}
          data-testid={`admin-images-preview-${tid}`}
          className="max-h-32 w-full object-contain rounded border border-[#262626] bg-black" />
      ) : (
        <div className="h-24 grid place-items-center rounded border border-dashed border-[#262626] text-slate-500">
          <ImageIcon size={20} />
        </div>
      )}
      <div className="space-y-2 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono text-slate-400">Görsel {n} · blok #{b.block_index + 1}</span>
          <span data-testid={`admin-images-status-${tid}`} className={`text-[10px] px-2 py-0.5 rounded-full ${st.cls}`}>{st.label}</span>
        </div>
        <p className="text-xs text-slate-300 whitespace-pre-wrap break-words" data-testid={`admin-images-desc-${tid}`}>
          {b.description || "Açıklama yok"}
        </p>
        <input ref={ref} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" onChange={onFile}
          data-testid={`admin-images-file-${tid}`} />
        <div className="flex gap-2">
          <button onClick={() => ref.current?.click()} disabled={busy} data-testid={`admin-images-upload-${tid}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-[#EA580C] hover:bg-[#F97316] text-white text-xs disabled:opacity-50">
            <Upload size={12} /> {busy ? "Yükleniyor…" : b.image_path ? "Değiştir" : "Görsel yükle"}
          </button>
          {b.image_path && (
            <button onClick={remove} disabled={busy} data-testid={`admin-images-remove-${tid}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 text-xs disabled:opacity-50">
              <Trash2 size={12} /> Kaldır
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
